import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { DEFAULT_DOMAIN_SUFFIX } from '../../../globals';
import { ApiErrorFactory } from '../../core/api-errors';
import { getCookie } from '../../core/cookie';
import { DefaultDomainUtility } from '../../core/domain-utility';
import { SitesRouteParams } from '../../core/routeparams';
import { CreateTemplateResponse } from '../../core/template/template';
import { FormFields } from '../create-progress/form-fields';
import { ErrorDialog } from '../dialog';
import { DomainMessagesService } from '../domain/domain-messages.service';
import { CreateNewTemplateService } from './create-new-template.service';

@Component({
  selector: 'app-create-new-template',
  templateUrl: './create-new-template.component.html',
  styleUrls: ['./create-new-template.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CreateNewTemplateComponent implements OnDestroy {
  formFields: FormFields = new FormFields();
  createDisabled = false;
  defaultDomainSuffix: string;
  maxLength = 150;

  private accountGroupId: string;
  private partnerId: string;
  private dialogRef: MatDialogRef<ErrorDialog>;
  private errorDialogRef: MatDialogRef<ErrorDialog>;
  private subscriptions: Subscription[] = [];

  constructor(
    public errorDialog: MatDialog,
    public dialog: MatDialog,
    protected router: Router,
    protected route: ActivatedRoute,
    private translate: TranslateService,
    private apiErrorFactory: ApiErrorFactory,
    private createNewTemplateService: CreateNewTemplateService,
    private domainMessages: DomainMessagesService,
  ) {
    this.defaultDomainSuffix = DEFAULT_DOMAIN_SUFFIX;
    this.partnerId = (this.route.snapshot.params as SitesRouteParams).partnerId;
    this.formFields.siteTitle = '';
    this.formFields.defaultDomain = this.partnerId.toLowerCase() + '-' + new Date().getTime().toString(36);
    this.accountGroupId = getCookie('account_id');
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  calculateRemainingChars(currentChars: number): number {
    return this.maxLength - currentChars;
  }

  calculateRemainingSubdomainChars(currentChars: number): number {
    return DefaultDomainUtility.maxDefaultLabelLength - currentChars;
  }

  handleError(errorResponse: HttpErrorResponse): void {
    const parsedError = this.apiErrorFactory.parseErrorResponse(errorResponse);
    this.displayError(parsedError.getUserMessage());
  }

  public onSubmit(): void {
    this.createDisabled = true;
    try {
      this.formFields.clean();
      let message;
      if (this.formFields.siteTitle && this.formFields.defaultDomain) {
        message = this.domainMessages.getInvalidDefaultDomainMessage(this.formFields.defaultDomain);
      } else {
        message = this.translate.instant('PAGES.CREATE.ERRORS.ALL_FIELDS_ARE_REQUIRED');
      }
      if (message) {
        this.createDisabled = false;
        this.displayErrorMessage(message);
        return;
      }
      this.createNewTemplateService.setFormFields(this.formFields);
      this.subscriptions.push(
        this.createNewTemplateService.createTemplate(this.partnerId).subscribe(
          (res: CreateTemplateResponse) => {
            const url = `/create/${this.accountGroupId}/create/${res.templateId}/job/${res.jobId}/template`;
            this.createNewTemplateService.navigateTo(url);
          },
          (err) => {
            this.createDisabled = false;
            this.handleError(err);
          },
        ),
      );
    } catch (error) {
      this.createDisabled = false;
      this.handleError(error);
    }
  }

  private displayError(error: string): void {
    this.dialogRef = this.dialog.open(ErrorDialog, {
      data: { errMsg: error },
    });
  }

  private displayErrorMessage(message: string): void {
    this.errorDialogRef = this.errorDialog.open(ErrorDialog, {
      data: { errMsg: message },
    });
  }
}
